/* eslint-disable */
import { toast } from 'sonner';
import { useState } from 'react';
import { format } from 'date-fns';
import { ColumnDef } from '@tanstack/react-table';
import { Ban, Check, Pencil, Trash2 } from 'lucide-react';
import {
  ProductOffer,
  ProductOfferOrder,
} from '@durma-soft/gros-sdk/dist/types/product-offers';
import {
  useAuthUserRestaurant,
  useCancelProductOfferOrderMutation,
  ValidationError,
} from '@durma-soft/gros-sdk';

import { Button } from '@/components/shared/shadcn-ui/button';
import { ConfirmationModal } from '@/components/shared/confirmation-modal';
import { ProductOfferImage } from '@/components/restaurant/product-offers/product-offer-image';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shared/shadcn-ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/shared/shadcn-ui/tooltip';

import { TType } from '@/types/general';
import { formatPrice, numericSort } from '@/utils/product-offers';

export const offerColumns = (
  t: TType,
  onOrder: (offer: ProductOffer) => void,
): ColumnDef<ProductOffer>[] => [
  {
    accessorKey: 'image',
    enableSorting: false,
    header: t('common.image'),
    cell: (cellData) => {
      const { image, name } = cellData.row.original;
      return <ProductOfferImage image={image} name={name} />;
    },
  },
  {
    accessorKey: 'name',
    header: t('common.name'),
  },
  {
    id: 'supplier',
    header: t('product-offers.supplier'),
    accessorFn: (row) => row.supplier?.name ?? '',
    cell: (cellData) => cellData.row.original.supplier?.name ?? '—',
  },
  {
    accessorKey: 'price_per_unit',
    header: t('product-offers.price-per-unit'),
    sortingFn: numericSort,
    cell: (cellData) => {
      const { price_per_unit, currency } = cellData.row.original;
      return `${formatPrice(price_per_unit)} ${currency}`;
    },
  },
  {
    accessorKey: 'unit',
    header: t('product-offers.unit'),
  },
  {
    accessorKey: 'order',
    enableSorting: false,
    header: '',
    cell: (cellData) => (
      <Button onClick={() => onOrder(cellData.row.original)}>
        {t('product-offers.order')}
      </Button>
    ),
  },
];

export const ownOrderColumns = (
  t: TType,
  onEdit: (order: ProductOfferOrder) => void,
): ColumnDef<ProductOfferOrder>[] => [
  {
    accessorKey: 'created_at',
    header: t('common.date'),
    cell: (cellData) =>
      format(new Date(cellData.row.original.created_at), 'dd.MM.yyyy HH:mm'),
  },
  {
    id: 'product',
    header: t('common.name'),
    cell: (cellData) => cellData.row.original.product_offer.name,
  },
  {
    id: 'supplier',
    header: t('product-offers.supplier'),
    accessorFn: (row) => row.product_offer.supplier?.name ?? '',
    cell: (cellData) =>
      cellData.row.original.product_offer.supplier?.name ?? '—',
  },
  {
    accessorKey: 'quantity',
    header: t('common.quantity'),
    sortingFn: numericSort,
    cell: (cellData) =>
      `${Number(cellData.row.original.quantity)} ${cellData.row.original.unit}`,
  },
  {
    accessorKey: 'price_per_unit',
    header: t('product-offers.price-per-unit'),
    sortingFn: numericSort,
    cell: (cellData) =>
      `${formatPrice(cellData.row.original.price_per_unit)} ${cellData.row.original.currency}`,
  },
  {
    id: 'total',
    enableSorting: false,
    header: t('product-offers.total'),
    cell: (cellData) => {
      const { price_per_unit, quantity, currency } = cellData.row.original;
      return `${(Number(price_per_unit) * Number(quantity)).toFixed(2)} ${currency}`;
    },
  },
  {
    id: 'status',
    enableSorting: false,
    header: t('product-offers.status'),
    cell: (cellData) => {
      const { confirmed_at, denied_at, denial_reason, deleted_at } =
        cellData.row.original;
      if (deleted_at) {
        return (
          <span className="text-muted-foreground">
            {t('product-offers.cancelled')}
          </span>
        );
      }
      if (denied_at) {
        return (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-destructive"
              >
                <Ban className="w-4 h-4" />
                {t('product-offers.denied')}
              </button>
            </PopoverTrigger>
            <PopoverContent className="max-w-xs text-sm">
              {denial_reason}
            </PopoverContent>
          </Popover>
        );
      }
      if (confirmed_at) {
        return (
          <span className="inline-flex items-center gap-1">
            <Check className="w-4 h-4" />
            {t('product-offers.confirmed')}
          </span>
        );
      }
      return t('product-offers.pending');
    },
  },
  {
    id: 'actions',
    enableSorting: false,
    header: '',
    cell: (cellData) => {
      const restaurant = useAuthUserRestaurant();

      const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
        useState(false);

      const cancelOrder = useCancelProductOfferOrderMutation();

      const order = cellData.row.original;

      // Confirmed, denied, and cancelled orders are locked
      if (order.confirmed_at || order.denied_at || order.deleted_at) return null;

      const handleCancel = async () => {
        try {
          await cancelOrder.mutateAsync({
            restaurant_id: restaurant.id,
            product_offer_order_id: order.id,
          });
          toast.success(t('product-offers.cancel-order-success-msg'));
        } catch (error) {
          if (error instanceof ValidationError) {
            toast.error(error.message);
          } else {
            toast.error(t('product-offers.cancel-order-error-msg'));
          }
        } finally {
          setIsConfirmationModalOpen(false);
        }
      };

      return (
        <TooltipProvider delayDuration={100}>
          <div className="flex items-center justify-center gap-5">
            {/* Edit quantity */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center justify-center text-gray-700 p-0"
                  onClick={() => onEdit(order)}
                >
                  <Pencil className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('product-offers.edit-order')}</TooltipContent>
            </Tooltip>
            {/* Cancel */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center justify-center text-gray-700 p-0"
                  onClick={() => setIsConfirmationModalOpen(true)}
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('product-offers.cancel-order')}
              </TooltipContent>
            </Tooltip>
          </div>
          <ConfirmationModal
            isLoading={cancelOrder.isPending}
            isModalOpen={isConfirmationModalOpen}
            confirmationText={t('product-offers.cancel-order-confirm')}
            title={t('product-offers.cancel-order')}
            description={t('product-offers.cancel-order-description', {
              productOffer: order.product_offer.name,
            })}
            onConfirm={handleCancel}
            onCancel={() => setIsConfirmationModalOpen(false)}
            closeModal={() => setIsConfirmationModalOpen(false)}
          />
        </TooltipProvider>
      );
    },
  },
];
